/**
 * Agent Productivity Per Interval — the Analytics view.
 *
 * A compact summary of the visible rows: total calls, talk/handle/wait
 * time totals and the agent state totals for the selected window.
 */
import { useMemo, type JSX } from 'react'
import { Chart } from 'primereact/chart'
import { Activity, CalendarClock, Phone, Timer, Users } from 'lucide-react'

import {
  AnalyticsCanvas,
  BarList,
  CardEmpty,
  CardGrid,
  ChartCard,
  HeroBand,
  KpiRow,
  Meter,
  SectionLabel,
  StatTile,
  type BarListItem,
} from '../analytics/AnalyticsKit.js'
import {
  VIZ,
  formatCount,
  formatPct,
  formatSeconds,
  lineDataset,
  lineOptions,
  ratePct,
} from '../analytics/viz.js'
import {
  bucketLabel,
  bucketSeries,
  peakOf,
  sumBoardBy,
} from './analytics-helpers.js'

export default function AgentProductivityPerIntervalAnalytics({
  rows,
}: {
  rows: readonly Record<string, unknown>[]
}): JSX.Element {
  const sums = useMemo(() => {
    const numCol = (key: string): number =>
      rows.reduce((sum, row) => {
        const v = row[key]
        const n = typeof v === 'number' ? v : Number(v ?? 0)
        return sum + (Number.isFinite(n) ? n : 0)
      }, 0)

    return {
      totalCalls: numCol('totalCalls'),
      totalInbound: numCol('totalInbound'),
      totalOutbound: numCol('totalOutbound'),
      answeredTotal: numCol('answeredTotal'),
      totalAbandoned: numCol('totalAbandoned'),
      inboundTalk: numCol('totalInboundTalkTime'),
      outboundTalk: numCol('totalOutboundTalkTime'),
      inboundHandle: numCol('totalInboundHandleTime'),
      outboundHandle: numCol('totalOutboundHandleTime'),
      waitTime: numCol('totalWaitTime'),
      login: numCol('loginTime'),
      ready: numCol('readyTime'),
      available: numCol('availableTime'),
      breakTime: numCol('breakTime'),
      agents: new Set(rows.map((r) => r['agentId'])).size,
    }
  }, [rows])

  const series = useMemo(() => bucketSeries(rows, 'bucketStart', 'totalCalls'), [rows])
  const busiest = useMemo(() => peakOf(series), [series])

  const trendData = useMemo(
    () => ({
      labels: series.labels,
      datasets: [lineDataset('Calls', series.values, VIZ.series[0], { fill: true })],
    }),
    [series],
  )

  const agentItems = useMemo<BarListItem[]>(() => {
    const board = sumBoardBy(rows, 'agentName', 'totalCalls', { limit: 10 })
    return board.map((entry) => ({
      label: entry.label,
      value: entry.value,
      meta: formatPct(ratePct(entry.value, sums.totalCalls), 1),
    }))
  }, [rows, sums.totalCalls])

  const campaignItems = useMemo<BarListItem[]>(() => {
    const board = sumBoardBy(rows, 'campaignName', 'totalCalls', { limit: 6 })
    return board.map((entry) => ({
      label: entry.label,
      value: entry.value,
      meta: formatPct(ratePct(entry.value, sums.totalCalls), 1),
    }))
  }, [rows, sums.totalCalls])

  const answerRate = useMemo(() => ratePct(sums.answeredTotal, sums.totalCalls), [sums])
  const abandonRate = useMemo(() => ratePct(sums.totalAbandoned, sums.totalCalls), [sums])

  if (rows.length === 0) {
    return (
      <AnalyticsCanvas>
        <div className="rounded-[4px] border border-slate-200 bg-white px-6 py-16 text-center">
          <p className="text-sm font-semibold text-slate-900">Nothing to analyse</p>
          <p className="mt-1 text-xs text-slate-500">
            The current filters returned no agent activity. Widen the window or clear the
            filters, then run the report again.
          </p>
        </div>
      </AnalyticsCanvas>
    )
  }

  return (
    <AnalyticsCanvas>
      <HeroBand
        eyebrow="Calls handled"
        value={formatCount(sums.totalCalls)}
        caption={`${sums.agents} agent${sums.agents === 1 ? '' : 's'} · ${rows.length} interval${rows.length === 1 ? '' : 's'}`}
        spark={series.values}
        Icon={Users}
        facts={[
          { label: 'Inbound', value: formatCount(sums.totalInbound) },
          { label: 'Outbound', value: formatCount(sums.totalOutbound) },
          { label: 'Answered', value: formatCount(sums.answeredTotal) },
          { label: 'Abandoned', value: formatCount(sums.totalAbandoned) },
        ]}
      />

      <KpiRow>
        <StatTile label="Inbound talk" value={formatSeconds(sums.inboundTalk)} />
        <StatTile label="Outbound talk" value={formatSeconds(sums.outboundTalk)} />
        <StatTile label="Inbound handle" value={formatSeconds(sums.inboundHandle)} />
        <StatTile label="Outbound handle" value={formatSeconds(sums.outboundHandle)} />
        <StatTile label="Total wait" value={formatSeconds(sums.waitTime)} />
        <StatTile
          label="Answer rate"
          value={formatPct(answerRate, 1)}
          tone={(answerRate ?? 0) >= 80 ? 'good' : 'warning'}
        />
      </KpiRow>

      {/* ── When ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel note={`${series.labels.length} interval${series.labels.length === 1 ? '' : 's'}`}>
          Call volume over time
        </SectionLabel>
        <CardGrid>
          <ChartCard
            title="Calls per interval"
            subtitle="Total calls handled, on the interval chosen in the filters"
            Icon={CalendarClock}
            wide
            height={280}
            footnote={
              busiest
                ? `Busiest interval: ${bucketLabel(busiest.label)} with ${formatCount(busiest.value)} calls.`
                : undefined
            }
          >
            {series.labels.length === 0 ? (
              <CardEmpty message="No row in this result set carries an interval." />
            ) : (
              <Chart
                type="line"
                data={trendData}
                options={lineOptions({ tickLabel: bucketLabel })}
                className="h-full w-full"
              />
            )}
          </ChartCard>
        </CardGrid>
      </section>

      {/* ── Who ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel>By agent and campaign</SectionLabel>
        <CardGrid>
          <ChartCard title="Calls per agent" subtitle="Total calls handled by each agent" Icon={Users} wide>
            <BarList items={agentItems} format={formatCount} emptyMessage="No agent data in this window." />
          </ChartCard>
          <ChartCard title="Calls per campaign" subtitle="Total calls by campaign" Icon={Phone}>
            <BarList items={campaignItems} format={formatCount} emptyMessage="No campaign data in this window." />
          </ChartCard>
        </CardGrid>
      </section>

      {/* ── State & Direction ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel>Agent states and call direction</SectionLabel>
        <CardGrid>
          <ChartCard title="Time in state" subtitle="Total seconds per agent state" Icon={Activity}>
            <div className="grid grid-cols-2 gap-4 p-4">
              <StatTile label="Login" value={formatSeconds(sums.login)} />
              <StatTile label="Ready" value={formatSeconds(sums.ready)} />
              <StatTile label="Available" value={formatSeconds(sums.available)} />
              <StatTile label="Break" value={formatSeconds(sums.breakTime)} />
            </div>
          </ChartCard>

          <ChartCard title="Volume by direction" subtitle="Calls split by direction" Icon={Phone}>
            <div className="grid grid-cols-2 gap-4 p-4">
              <StatTile label="Inbound" value={formatCount(sums.totalInbound)} />
              <StatTile label="Outbound" value={formatCount(sums.totalOutbound)} />
            </div>
          </ChartCard>

          <ChartCard title="Answer vs abandon" subtitle="Outcome distribution" Icon={Timer} height={220}>
            <Meter
              segments={[
                { label: 'Answered', value: sums.answeredTotal, color: VIZ.series[0] },
                { label: 'Abandoned', value: sums.totalAbandoned, color: VIZ.status.critical },
              ]}
              emptyMessage="No calls in this window."
            />
            <div className="grid grid-cols-2 gap-4 px-4 pb-4">
              <StatTile
                label="Answer rate"
                value={formatPct(answerRate, 1)}
                tone={(answerRate ?? 0) >= 80 ? 'good' : 'warning'}
              />
              <StatTile
                label="Abandon rate"
                value={formatPct(abandonRate, 1)}
                tone={(abandonRate ?? 0) > 5 ? 'critical' : 'good'}
              />
            </div>
          </ChartCard>
        </CardGrid>
      </section>
    </AnalyticsCanvas>
  )
}
